// @noflow
import { ClickableArea, Group, InnerElement, Label, OuterElement } from './style';
import { EVENT_HEIGHT_MARGIN_RATIO, EVENT_SIDE_MARGIN } from '../constants';
import { injectIntl, intlShape } from 'react-intl';

import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import * as React from 'react';
import { scheduleModesEnum } from '~/core/constants/schedule';

const ADDED_CLICKABLE_WIDTH = 50; // needed when the event is tiny and so hard to select the event
const MIN_TEXT_WIDTH = 40; // the number of pixels needed to display the value

Event.propTypes = {
    canBeDeleted: PropTypes.bool.isRequired,
    containerWidth: PropTypes.number.isRequired,
    dualColors: PropTypes.object,
    event: ImmutablePropTypes.contains({
        value: ImmutablePropTypes.map.isRequired
    }).isRequired,
    eventColor: PropTypes.string.isRequired,
    getLabel: PropTypes.func.isRequired,
    height: PropTypes.number.isRequired,
    intl: intlShape,
    isCurrentEvent: PropTypes.bool.isRequired,
    isDayDragging: PropTypes.bool.isRequired,
    isDayShrunk: PropTypes.bool.isRequired,
    isFirstEvent: PropTypes.bool.isRequired,
    isLastEvent: PropTypes.bool.isRequired,
    isSelected: PropTypes.bool.isRequired,
    isSelectedDay: PropTypes.bool.isRequired,
    left: PropTypes.number.isRequired,
    scheduleMode: PropTypes.string.isRequired,
    scheduleType: PropTypes.string.isRequired,
    selectEvent: PropTypes.func.isRequired,
    sideMarginWidth: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired
};

function Event({
    canBeDeleted,
    containerWidth,
    dualColors,
    event,
    eventColor,
    getLabel,
    height,
    intl: { formatMessage },
    isCurrentEvent,
    isDayDragging,
    isDayShrunk,
    isFirstEvent,
    isLastEvent,
    isSelected,
    isSelectedDay,
    left,
    scheduleMode,
    scheduleType,
    selectEvent,
    sideMarginWidth,
    top,
    width
}) {
    const eventWidth = Math.max(width - 2 * EVENT_SIDE_MARGIN, 0);
    const heightMargin = Math.floor(height * EVENT_HEIGHT_MARGIN_RATIO);
    const eventHeight = height - 2 * heightMargin;
    let radius = Math.min(Math.floor(eventHeight / 2), Math.floor(eventWidth / (isFirstEvent || isLastEvent ? 1 : 2)));
    if (radius === 1) {
        // it seems for really small events the radius of 1 make the event to not be rendered
        radius = 0;
    }

    const x = EVENT_SIDE_MARGIN - (isFirstEvent ? radius : 0);
    const displayWidth = eventWidth + (isFirstEvent ? radius : 0) + (isLastEvent ? radius : 0);
    const isTinyEvent = displayWidth === 0 && !isFirstEvent;
    const isLabelOnTheLeft = containerWidth - sideMarginWidth - (left + width) < MIN_TEXT_WIDTH;
    const textXPos = width >= MIN_TEXT_WIDTH ? width / 2 : isLabelOnTheLeft ? -5 : width + 5;

    const eventLabel = getLabel(formatMessage);
    const TextComponent = props => (
        <Label
            dy="5"
            hide={isDayShrunk || (width < MIN_TEXT_WIDTH && !isSelectedDay)}
            isCurrentEvent={isCurrentEvent}
            isDualColors={!!dualColors}
            isSelected={isSelected}
            textPosition={width >= MIN_TEXT_WIDTH ? 'middle' : isLabelOnTheLeft ? 'end' : 'start'}
            valueColor={eventColor}
            {...props}
        />
    );

    const addedClickableWidth = isSelectedDay ? ADDED_CLICKABLE_WIDTH : 0;

    return (
        <Group
            data-qa="schedule-event"
            isDayDragging={isDayDragging}
            style={{ transform: `translate3d(${left}px, ${top}px, 0)` }}
            transform={`translate(${left} ${top})`}
        >
            <OuterElement
                blurEvent={isDayShrunk}
                canBeDeleted={canBeDeleted}
                dash={canBeDeleted}
                height={eventHeight}
                isCurrentEvent={isCurrentEvent}
                isDayDragging={isDayDragging}
                isDualColors={!!dualColors}
                isSelected={isSelected}
                isSelectedDay={isSelectedDay}
                isTinyEvent={isTinyEvent}
                rx={radius}
                ry={radius}
                style={{
                    transform: `translate3d(${x}px, ${heightMargin}px, 0)`
                }}
                transform={`translate(${x} ${heightMargin})`}
                valueColor={eventColor}
                width={isTinyEvent ? 1 : displayWidth}
            />
            {isCurrentEvent && (
                <InnerElement
                    blurEvent={isDayShrunk}
                    height={Math.max(eventHeight - 8, 0)}
                    isDayDragging={isDayDragging}
                    isDualColors={!!dualColors}
                    isSelected={isSelected}
                    isSelectedDay={isSelectedDay}
                    rx={Math.max(radius - 4, 0)}
                    ry={Math.max(radius - 4, 0)}
                    style={{
                        transform: `translate3d(${x + 4}px, ${heightMargin + 4}px, 0)`
                    }}
                    transform={`translate(${x + 4} ${heightMargin + 4})`}
                    valueColor={eventColor}
                    width={Math.max(displayWidth - 8, 0)}
                />
            )}
            <Group
                isDayDragging={isDayDragging}
                style={{
                    transform: `translate3d(${textXPos}px, ${height / 2}px, 0)`
                }}
                transform={`translate(${textXPos} ${height / 2})`}
            >
                {eventLabel.topValue && (
                    <TextComponent
                        dy={5 - eventHeight * 0.23}
                        valueColor={dualColors ? dualColors.topColor : eventColor}
                    >
                        {eventLabel.topValue}
                    </TextComponent>
                )}
                {eventLabel.bottomValue && (
                    <TextComponent
                        dy={5 + eventHeight * 0.23}
                        valueColor={dualColors ? dualColors.bottomColor : eventColor}
                    >
                        {eventLabel.bottomValue}
                    </TextComponent>
                )}
                {!eventLabel.topValue && !eventLabel.bottomValue && <TextComponent dy="5">{eventLabel}</TextComponent>}
            </Group>
            <ClickableArea
                height={height}
                isClickable={
                    scheduleMode === scheduleModesEnum.DEFAULT ||
                    (scheduleMode === scheduleModesEnum.DELETE_EVENT && canBeDeleted)
                }
                onClick={selectEvent}
                style={{
                    transform: `translate3d(-${addedClickableWidth / 2}px, 0, 0)`
                }}
                transform={`translate(-${addedClickableWidth / 2} 0)`}
                width={width + addedClickableWidth}
            />
        </Group>
    );
}

export default injectIntl(Event);



// WEBPACK FOOTER //
// ./src/components/widgets/schedule/event/component.js